import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { environment } from '../../../../environments/environment';
import { DeepLinkConfig, DeepLinkRoute, UniversalLinkRoute } from '../../models/deep-link.model';
import { PlatformUtil } from '../../utils/platform/platform.util';
import { MsalRedirectService } from '../auth/msal-redirect.service';

@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private readonly router = inject(Router);
  private readonly msalRedirectService = inject(MsalRedirectService);

  private routes: DeepLinkRoute[] = [];
  private universalLinks: UniversalLinkRoute[] = [];
  private lastProcessedUrl: string | null = null;

  private readonly nativeRedirectUri: string | undefined =
    environment.msalConfig.auth.nativeRedirectUri;

  private readonly redirectUri: string = environment.msalConfig.auth.redirectUri;

  /**
   * Initialize the deep link service with the given configuration.
   * @param config The deep link configuration
   * @returns void
   */
  initialize(config: DeepLinkConfig): void {
    if (!this.nativeRedirectUri) {
      throw new Error('Missing environment.msalConfig.auth.nativeRedirectUri');
    }

    this.routes = config.routes;
    this.universalLinks = config.universalLinks || [];

    if (!PlatformUtil.isNative()) return;

    this.setupListeners();
    void App.getLaunchUrl().then((launch) => {
      if (launch?.url) this.handleUrl(launch.url);
    });
  }

  /**
   * Public entry to process an incoming deep link URL.
   * Idempotent for the same URL value; repeated calls with the last processed URL are ignored.
   * @param url The deep link URL
   */
  public handleUrl(url: string): void {
    if (!url) return;
    if (this.lastProcessedUrl === url) return;

    this.lastProcessedUrl = url;

    if (this.nativeRedirectUri && url.startsWith(this.nativeRedirectUri)) {
      this.handleAuthRedirect(url);
      return;
    }

    const customRoute = this.routes.find((r) => url === `${r.scheme}://${r.path}`);
    if (customRoute) {
      this.navigateToCustomRoute(customRoute);
      return;
    }

    const universal = this.matchUniversalLink(url);
    if (universal) {
      this.navigateToUniversalRoute(universal.route, universal.url);
    }
  }

  /**
   * Set up listeners for app URL opens and state changes.
   */
  private setupListeners(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      if (event?.url) this.handleUrl(event.url);
    });

    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      void App.getLaunchUrl().then((result) => {
        if (result?.url) this.handleUrl(result.url);
      });
    });
  }

  /**
   * Extract fragment (portion after '#') or empty string
   * @param url The URL to extract the fragment from
   * @returns The extracted fragment or an empty string
   */
  private handleAuthRedirect(url: string): void {
    // Android custom scheme intents strip the fragment portion (#...), causing MSAL to miss auth results.
    // Detect presence of auth query parameters so we can retry MSAL redirect processing before falling back.
    const fragment = this.extractFragment(url);
    const hasAuthQuery = /[?&](code|id_token|access_token)=/i.test(url);

    if (hasAuthQuery && !fragment) {
      try {
        const queryIndex = url.indexOf('?');

        if (queryIndex > -1) {
          const query = url.substring(queryIndex + 1);
          const base = `${window.location.origin}${this.redirectUri}`;
          const synthetic = `${base}?${query}`;

          window.history.replaceState(null, '', synthetic);
          // Also set hash to query string if MSAL relies on hash parsing (implicit style) on native.
          if (!window.location.hash) {
            window.location.hash = query;
          }
        }
      } catch {
        /* ignore history replacement errors */
      }
    }

    if (this.msalRedirectService.wasRecentLogout()) {
      void this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    if (fragment) {
      if (this.msalRedirectService.handleAuthErrorFragment(fragment)) return; // already navigated due to error fragment
      window.location.hash = fragment; // expose to MSAL
    }

    this.msalRedirectService.processMsalRedirect(fragment, hasAuthQuery);
  }

  /**
   * Extract fragment (portion after '#') or empty string
   * @param url The URL to extract the fragment from
   * @returns The extracted fragment or an empty string
   */
  private extractFragment(url: string): string {
    const idx = url.indexOf('#');
    return idx > -1 ? url.substring(idx + 1) : '';
  }

  /**
   * Handle navigation to a custom route
   * @param route
   */
  private navigateToCustomRoute(route: DeepLinkRoute): void {
    this.router
      .navigate([route.route], {
        ...(route.queryParams && { queryParams: route.queryParams }),
        ...(route.replaceUrl !== undefined && { replaceUrl: route.replaceUrl }),
      })
      .catch((err) => this.handleNavigationError(err));
  }

  /**
   *  match a URL against the configured universal links
   * @param url
   * @returns
   */
  private matchUniversalLink(url: string): { route: UniversalLinkRoute; url: URL } | null {
    try {
      const parsed = new URL(url);
      const normalizedPath = this.normalizePath(parsed.pathname);
      const match = this.universalLinks.find((link) => {
        const domainMatch =
          parsed.hostname === link.domain || parsed.hostname === `www.${link.domain}`;
        if (!domainMatch) return false;

        const linkPathNorm = this.normalizePath(link.path);

        // Exact match or (for non-root paths) the incoming path starts with the configured path + '/'
        const exact = normalizedPath === linkPathNorm;
        const prefix = linkPathNorm !== '/' && normalizedPath.startsWith(linkPathNorm + '/');
        return exact || prefix;
      });

      return match ? { route: match, url: parsed } : null;
    } catch {
      return null;
    }
  }

  /**
   * Normalize a path by removing a single trailing slash (except for root) to make matching more forgiving.
   */
  private normalizePath(pathname: string): string {
    if (!pathname) return '/';
    if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
    return pathname;
  }

  /**
   * Handle navigation to a universal link route
   * @param route
   * @param originalUrl
   */
  private navigateToUniversalRoute(route: UniversalLinkRoute, parsed: URL): void {
    const queryParams: Record<string, string> = { ...(route.queryParams || {}) };
    parsed.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    // Handle special case for invite links: /invite/{brokerName}
    if (route.path === '/invite') {
      const pathname = this.normalizePath(parsed.pathname);
      const parts = pathname.split('/').filter(Boolean);
      if (parts.length >= 2) {
        const encodedName = parts.slice(1).join('/');
        try {
          queryParams['invitedBy'] = decodeURIComponent(encodedName);
        } catch {
          queryParams['invitedBy'] = encodedName;
        }
      }
    }

    this.router
      .navigate([route.route], {
        ...(Object.keys(queryParams).length && { queryParams }),
        ...(route.replaceUrl !== undefined && { replaceUrl: route.replaceUrl }),
      })
      .catch((err) => this.handleNavigationError(err));
  }

  /**
   * Handle navigation errors
   * @param err
   * @returns
   */
  private handleNavigationError(err: unknown): void {
    if ((err as { ngNavigationCancellingError?: boolean }).ngNavigationCancellingError) return;
  }
}
